/**
 * Community LeftChart Top3 panel
 */
import React, { useMemo, useState } from 'react';
import { useHistory } from 'umi';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import { dealwithBiLiBiLiImage } from '@op/utils/cdnUrl';
import { getFeedsContentText } from '@op/utils/languageValue';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { goToFeedsReviewsDetail } from '@op/utils/url';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { ICommunityTimelineTopItem } from '@op/services/keyMetrics';
import { METRIC_SORT_LABEL } from '../constants/topPanel';
import './index.scss';
import CardCommon from '@op/components/CardCommon';
import TextClamp from '@opm/components/TextClamp';
import ChannelLabel from '@op/components/ChannelLabel';
import SvgIcon from '@home/components/SvgIcon';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import useLanguage from '@op/hooks/useLanguage';
import { LanguageType } from '@op/types/common';
import { JumpType } from '@home/utils/utils';

const RANK_LABELS = ['1st', '2nd', '3rd'];

export interface ITopPanelProps {
  visible: boolean;
  loading: boolean;
  list: ICommunityTimelineTopItem[];
  metric: string;
  clickParams: Record<string, any>;
  dateType: string;
  position: { left: number; top: number };
  onMore: (language: LanguageType) => void;
  gameInfo?: any;
}

function getCoverImage(record: ICommunityTimelineTopItem): string {
  try {
    const videoDetail = JSON.parse(record.video_detail || '{}');
    if (videoDetail?.cover_image) {
      return dealwithBiLiBiLiImage(videoDetail.cover_image);
    }
  } catch {
    // ignore invalid video_detail
  }
  return DefaultImg.VIDEO;
}

export default function TopPanel({
  visible,
  loading,
  list,
  metric,
  clickParams,
  dateType,
  position,
  onMore,
  gameInfo,
}: ITopPanelProps) {
  const { formatMessage, formatOptionsMessage, isEn } = useLanguage();
  const history = useHistory();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const dateTitle = useMemo(() => {
    if (!clickParams?.start_time || !clickParams?.end_time) return '';
    return DBO_TM.timeTitle({
      start_time: clickParams.start_time,
      end_time: clickParams.end_time,
      date_type: dateType,
    });
  }, [clickParams, dateType]);

  const sortLabel = METRIC_SORT_LABEL[metric] || metric;

  if (!visible) return null;

  const handlePostClick = (record: ICommunityTimelineTopItem) => {
    buriedBtnPoint('KMCM_Top3Post', {
      comment_uin: record.comment_uin,
      metric,
    });
    goToFeedsReviewsDetail(
      history,
      {
        comment_uin: record.comment_uin,
        comment_time: record.comment_time,
      },
      {
        language,
        gameid: gameInfo?.edition_unified_id,
      },
      JumpType.Window
    );
  };

  const handleMoreClick = () => {
    buriedBtnPoint('KMCM_Top3More', { metric });
    onMore(language);
  };

  const statsElement = (value: number, icon: string) => {
    return (
      <div className="dbo-community-top-panel__stats-element">
        <i className={`dbo-community-top-panel__stats-element-icon icon-${icon}`} />
        <span className="dbo-community-top-panel__stats-element-value">
          {DBO_V.reviews(value)}
        </span>
      </div>
    );
  };

  return (
    <CardCommon
      className="dbo-community-top-panel"
      style={{ left: position.left, top: 0 }}
      onClick={e => e.stopPropagation()}
      title={(
        <div className="dbo-community-top-panel__header">
          <span className="dbo-community-top-panel__title">
            {formatMessage({ id: 'Top 3 Reviews' })}
          </span>
          <span className="dbo-community-top-panel__subtitle">
            {dateTitle} {formatMessage({ id: 'Sort by' })}{isEn ? ' ' : ''}{formatMessage({ id: sortLabel })}
          </span>
        </div>
      )}
      dropdownsFilterBeforeElement={
        <span
          className="dbo-community-top-panel__more"
          onClick={handleMoreClick}
        >
          {formatMessage({ id: 'More' })}
          <SvgIcon type="arrow-right" className="card-slot-icon" />
        </span>
      }
      loading={loading}
      loadingHeight={308}
      empty={!loading && list.length === 0}
      emptyHeight={308}
    >
      <TranslateSelect
        langOptions={formatOptionsMessage(languageSelectOptions)}
        lang={language}
        isSingle={true}
        getChange={(e: any) => {
          setLanguage(e);
        }}
        defaultValueText={formatMessage({ id: 'Language' })}
        originalText={'Original Language'}
      />
      <div className="dbo-community-top-panel__list">
        {list.map((record, index) => {
          const title = getFeedsContentText(record, language);
          const cover = getCoverImage(record);
          const rankLabel = RANK_LABELS[index] || `${index + 1}`;

          return (
            <div
              key={record.comment_uin || index}
              className="dbo-community-top-panel__item"
              onClick={() => handlePostClick(record)}
            >
              <div className={`dbo-community-top-panel__thumb rank-${rankLabel}`}>
                <RenderImg
                  coverImage={cover}
                  defaultImage={DefaultImg.VIDEO}
                  className="dbo-community-top-panel__thumb-img"
                  mode="image"
                />
              </div>
              <div className="dbo-community-top-panel__content">
                <TextClamp
                  text={title}
                  rowNum={1}
                  className="dbo-community-top-panel__post-title"
                />
                <ChannelLabel channel_name={record.channel_name as any} className="dbo-community-top-panel__channel-label" />
                <div className="dbo-community-top-panel__stats">
                  {statsElement(record.view, 'play')}
                  {statsElement(record.tweets_like, 'like')}
                  {statsElement(record.tweets_reply, 'comment')}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CardCommon>
  );
}
